import {
  XyDataSeries,
  HorizontalLineAnnotation,
  ELabelPlacement,
  EAnnotationLayer,
  SciChartSurface,
  ZoomPanModifier,
  MouseWheelZoomModifier,
  ZoomExtentsModifier,
  RolloverModifier,
  RubberBandXyZoomModifier,
  EXyDirection,
  EExecuteOn,
  FastImpulseRenderableSeries,
  LegendModifier,
  EAutoRange,
  NumberRange,
  EAxisAlignment,
  NumericAxis,
  DateTimeNumericAxis,
  CursorModifier,
  IRenderableSeries,
  FastLineRenderableSeries,
  IDataSeries,
  ISciChartSurfaceBase,
  AUTO_COLOR,
  TrianglePointMarker,
  UpdateSuspender,
  FastBandRenderableSeries,
  XyyDataSeries,
  AxisBase2D,
} from "scichart";
import { IInitResult, TInitFunction } from "scichart-react";
import { ColumnPaletteProviderActivityChartAsset } from "../PaletteProvider";
import moment from "moment";
import { axisLabelDateFormatter } from "../../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter";
import { GetChartTheme } from "../../../../Shared/SciChart/ChartConfiguration";
import { LocalizationService } from "@progress/kendo-react-intl";
import { DateTickProvider } from "../../../../Shared/SciChart/SmartAxis/DateTickProvider";
import { SmartDateTZLabelProvider } from "../../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider";
import { createDateTzConverter } from "../../../../Shared/SciChart/SmartAxis/timezones";

export interface TAssetActivityChartConfigResult<TSurface extends ISciChartSurfaceBase> extends IInitResult<TSurface> {
  setAutoRange: (value: boolean) => void,
  changeChartType: (viewType: string, visibleSeriesCompliance) => void,
  changeDataSeries: (dataSeries: any) => void
}

export type TAssetActivityConfigFunc = TInitFunction<SciChartSurface, TAssetActivityChartConfigResult<SciChartSurface>>;

export interface Props {
  chartData: any
}

export interface ChartOptions {
  isAutoRange: boolean;
  isBarChartOn: boolean;
  visibleRange: { min: number | undefined; max: number | undefined; };
  setVisibleRange: (range: any) => void;
  setVisibleSeriesAsset: (series: any, isChecked: boolean) => void;
  visibleSeriesAsset: { [key: string]: boolean;};
  aggregator: string;
  viewType: string;
  visibleDataSeries: {
    pressureMinMax: boolean;
    pressureMean: boolean;
    temperature: boolean;
    volume: boolean;
  };
  isFullscreen?: boolean;
}

export const getChartAPI = (props:Props, chartOptions:ChartOptions,  localizationService: LocalizationService, currentLanguage: { locale: string; localeId: string; }) => {

  const endFilterDateUnix = moment(props.chartData.dateFilter.endDate).unix();
  const startFilterDateUnix = moment(props.chartData.dateFilter.startDate).unix();

  const theme = GetChartTheme();  
  
  const tableau20 = [
    "#7B2CBF", // 6 Deep Purple
    "#0066FF", // 1 Bold Blue
    "#FF0000", // 2 Strong Red
    "#00CC00", // 3 Pure Green
    "#FF00CC", // 4 Magenta
    "#00DDFF", // 5 Sky Cyan
    "#000000",  // Black
    "#FF8800", // 7 Tangerine
    "#004D99", // 8 Navy Blue
    "#CC00FF", // 9 Vivid Violet
    "#00FF66", // 10 Mint Green
    "#FF0066", // 11 Hot Pink
    "#66FF00", // 13 Lime Green
    "#0099CC", // 14 Aqua Blue
    "#CC0000", // 15 Deep Red
    "#666666", // 16 Bold Gray
    "#6A1B9A", // 17 Dark Purple
    "#FFCC00", // 18 Golden Yellow
    "#3366FF", // 19 Bright Blue
    "#228B22", // 20 Forest Green
  ];

  theme.strokePalette = tableau20;
  theme.fillPalette = tableau20.map(c => c + "33"); // Add transparency for fills

  let setAutoRangeCallback: (arg0: boolean) => void;
  let changeChartTypeCallback: (arg0: string, arg1:any) => void;
  let changeDataSeriesCallback: (arg0: any) => void;
  const configAfterInit = (initResult) => {
      setAutoRangeCallback = initResult.setAutoRange;
      changeChartTypeCallback = initResult.changeChartType;
      changeDataSeriesCallback = initResult.changeDataSeries;

      // Immediately apply the current legendConfig
      if (chartOptions.visibleDataSeries) {
        changeDataSeries(chartOptions.visibleDataSeries);
      }
  } 

  const setAutoRange = (value: boolean) => {
      if (setAutoRangeCallback !== undefined) {
          setAutoRangeCallback(value);
      }
  }

  const changeChartType = (viewType: string, visibleSeries) => {
    if (changeChartTypeCallback !== undefined) {
      changeChartTypeCallback(viewType, visibleSeries);
    }
  }

  const changeDataSeries = (dataSeries: any) => {
    if (changeDataSeriesCallback !== undefined) {
      changeDataSeriesCallback(dataSeries);
    }
  }

  const initChart : TAssetActivityConfigFunc = async (rootElement: string | HTMLDivElement) => {
      const {sciChartSurface, wasmContext} = await SciChartSurface.create(rootElement, {theme: theme});
  
    // #region Setup the XAxis
     const xAxis = new DateTimeNumericAxis(wasmContext, { 
      drawMajorBands: false, 
      drawMinorGridLines: false, 
      drawMajorGridLines: false, 
      visibleRange: new NumberRange(chartOptions.visibleRange.min, chartOptions.visibleRange.max),
      visibleRangeLimit: new NumberRange(startFilterDateUnix, endFilterDateUnix),
      growBy: new NumberRange(0.05, 0.05),
      labelStyle: {fontFamily:"Roboto", fontSize:12,}}
       );
   
      // Overrides getDeltaFromRange to get nice major and minor deltas for the ticks
      xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
     
      // Setting timezone to local system
      const updateSettings = (xAxis$: NumericAxis) => {
        const timezone = moment.tz.guess();
        // Calculate ticks which respect the time zone
        xAxis$.tickProvider = new DateTickProvider(wasmContext, timezone);
        // Draws nice axis labels timezone and locale specific
        xAxis$.labelProvider = new SmartDateTZLabelProvider(timezone,true, currentLanguage?.localeId);
        const dateTzConverter = createDateTzConverter(timezone);
        // This is needed for Cursor and Rollover chart modifiers
        xAxis$.labelProvider.formatCursorLabel = timestamp =>
          chartOptions.aggregator === 'none' ? axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter, currentLanguage?.localeId) : axisLabelDateFormatter.toFullDateOnly(timestamp, dateTzConverter, currentLanguage?.localeId);
        };
        // Setting timezone to local system
        updateSettings(xAxis);
   
      xAxis.visibleRangeChanged.subscribe((args) => { chartOptions.setVisibleRange(args?.visibleRange); });

      sciChartSurface.xAxes.add(xAxis);
      // #endregion
    // #region Setup the YAxis
    // Create the temperature axis first
    const yAxisTemperature = new NumericAxis(wasmContext, {
      axisAlignment: EAxisAlignment.Left, 
      stackedAxisLength: "20%",
      id: "Temp",
      drawMajorBands: false,
      drawMinorGridLines: false, 
      drawMajorGridLines: false, 
      labelPostfix: " °C",
      labelPrecision: 2,
      growBy: new NumberRange(0, 0.2),
      labelStyle: {fontFamily:"Roboto", fontSize:12},
      autoRange: EAutoRange.Always,  // Start with auto-range enabled
      visibleRangeLimit: new NumberRange(0, Number.MAX_VALUE)  // Keep the limit to prevent negative temperatures
    });

    // Add a subscription to ensure minimum stays at 0 for temperature
    yAxisTemperature.visibleRangeChanged.subscribe((args) => {
      if (args?.visibleRange) {
        // Temperature should never go below 0
        if (args.visibleRange.min !== 0) {
          const max = args.visibleRange.max > 0 ? args.visibleRange.max : 1;
          yAxisTemperature.visibleRange = new NumberRange(0, max);
        }
      }
    });

    // Create the pressure axis
    const yAxisPressure = new NumericAxis(wasmContext, {
      axisAlignment: EAxisAlignment.Right,
      drawMajorBands: false,
      drawMinorGridLines: true, 
      drawMajorGridLines: false, 
      labelPostfix:" psi",
      labelPrecision: 0,
      growBy: new NumberRange(0.2, 0.2),
      labelStyle: {fontFamily:"Roboto", fontSize:12},
      id: "DefaultAxisId",
      autoRange: EAutoRange.Always  // Start with auto-range enabled
    });

    // Add a subscription to ensure minimum stays at 0 unless we have negative values
    yAxisPressure.visibleRangeChanged.subscribe((args) => {
      if (args?.visibleRange) {
        // Only force min to 0 if we don't have any negative pressure values
        const hasNegativeValues = props.chartData.dataChart.some(endpoint => 
          endpoint.pressureData?.pressureDataMin?.some(val => val < 0)
        );
        
        if (!hasNegativeValues && args.visibleRange.min !== 0) {
          const max = args.visibleRange.max > 0 ? args.visibleRange.max : 1;
          yAxisPressure.visibleRange = new NumberRange(0, max);
        }
      }
    });

    // Check if we have any EndpointBF endpoints and if ALL are EndpointBF
    const hasEndpointBF = props.chartData.dataChart.some(endpoint => 
      endpoint.endpointType === 'EndpointBF'
    );
    const allAreEndpointBF = props.chartData.dataChart.every(endpoint => 
      endpoint.endpointType === 'EndpointBF'
    );

    // Create the main flow axis
    const yAxisFlow = new NumericAxis(wasmContext, {
      axisAlignment: EAxisAlignment.Right,
      drawMajorBands: false,
      drawMinorGridLines: false, 
      drawMajorGridLines: true,  
      labelPostfix: " cc", 
      labelPrecision: 2,
      growBy: new NumberRange(0, 0.1),
      labelStyle: {fontFamily:"Roboto", fontSize:12},
      id: "flowAxis"
    });

    // Create a separate volume axis for EndpointBF only if we have mixed endpoint types
    let yAxisFlowBF: NumericAxis | null = null;
    if (hasEndpointBF && !allAreEndpointBF) {
      yAxisFlowBF = new NumericAxis(wasmContext, {
        axisAlignment: EAxisAlignment.Left,
        drawMajorBands: false,
        drawMinorGridLines: false, 
        drawMajorGridLines: false, 
        labelPostfix: " cc", 
        labelPrecision: 2,
        growBy: new NumberRange(0, 0.1),
        labelStyle: {fontFamily:"Roboto", fontSize:12},
        id: "flowBFAxis"
      });
    }

    // Zero alignment logic for flow axes - only active when auto-range is OFF
    if (yAxisFlowBF) {
      // Add zero line annotations for visual reference
      const zeroLineFlow = new HorizontalLineAnnotation({
        y1: 0,
        yAxisId: "flowAxis",
        stroke: "gray",
        strokeThickness: 1,
        annotationLayer: EAnnotationLayer.BelowChart
      });

      const zeroLineFlowBF = new HorizontalLineAnnotation({
        y1: 0,
        yAxisId: "flowBFAxis",
        stroke: "gray", 
        strokeThickness: 1,
        annotationLayer: EAnnotationLayer.BelowChart
      });

      // Add both zero lines to the chart
      sciChartSurface.annotations.add(zeroLineFlow);
      sciChartSurface.annotations.add(zeroLineFlowBF);

      // Zero alignment function for flow axes
      const alignFlowZeros = (sourceAxis: NumericAxis, targetAxis: NumericAxis) => {
        try {
          const sourceVisible = sourceAxis.visibleRange;
          const targetVisible = targetAxis.visibleRange;

          // Calculate zero alignment when source axis has negative values
          if (sourceVisible.min < 0) {
            // Calculate where zero is positioned in source axis (as a fraction)
            const sourceZeroPosition = (0 - sourceVisible.min) / (sourceVisible.max - sourceVisible.min);

            // Target axis maintains its natural range but aligns zero proportionally
            const targetMax = targetVisible.max;
            let targetMin = -(targetMax * sourceZeroPosition / (1 - sourceZeroPosition));
            
            // Ensure target axis shows negative values
            if (targetMin > 0) {
              targetMin = -Math.abs(targetMin);
            }
            
            targetAxis.visibleRange = new NumberRange(targetMin, targetMax);
          }
        } catch (error) {
          console.error('Error in flow zero alignment:', error);
        }
      };

      // Only apply alignment when auto-range is OFF
      if (!chartOptions.isAutoRange) {
        // Initial alignment
        setTimeout(() => {
          if (yAxisFlowBF) {
            alignFlowZeros(yAxisFlowBF as NumericAxis, yAxisFlow);
          }
        }, 50);

        // Subscribe to changes for re-alignment
        yAxisFlow.visibleRangeChanged.subscribe(() => {
          if (!chartOptions.isAutoRange) {
            setTimeout(() => {
              if (yAxisFlowBF) {
                alignFlowZeros(yAxisFlowBF as NumericAxis, yAxisFlow);
              }
            }, 10);
          }
        });
      }
    }

          sciChartSurface.yAxes.add(yAxisFlow);
      if (yAxisFlowBF) {
        sciChartSurface.yAxes.add(yAxisFlowBF);
      }
      sciChartSurface.yAxes.add(yAxisTemperature);
      sciChartSurface.yAxes.add(yAxisPressure);
      
      // Only add zero lines for main axes when no BF axis exists (no alignment needed)
      if (!yAxisFlowBF) {
        const zeroLineFlowAlways = new HorizontalLineAnnotation({
          y1: 0,
          yAxisId: "flowAxis",
          stroke: "gray",
          strokeThickness: 1,
          annotationLayer: EAnnotationLayer.BelowChart
        });
        sciChartSurface.annotations.add(zeroLineFlowAlways);
      }
      
      // Always add zero line for pressure axis (no alignment logic for this axis)
      const zeroLinePressure = new HorizontalLineAnnotation({
        y1: 0,
        yAxisId: "DefaultAxisId",
        stroke: "gray",
        strokeThickness: 1,
        annotationLayer: EAnnotationLayer.BelowChart
      });
      sciChartSurface.annotations.add(zeroLinePressure);
   

    // #endregion
 
    // #region Setup the Chart Modifers
    const legend = new LegendModifier({
      placementDivId: chartOptions.isFullscreen ? "legend-asset-fullscreen" : "legend-asset",
      showCheckboxes: true,
      showSeriesMarkers: true,
      showLegend: true,
              // Subscribe to checked changed here
        isCheckedChangedCallback: (series: IRenderableSeries, isChecked: boolean) => {
         // Zoom extents for the specific y-axis this series belongs to
         const yAxisId = series.yAxisId || "flowAxis";
         const yAxis = sciChartSurface.yAxes.getById(yAxisId);
         if (yAxis) {
           yAxis.autoRange = EAutoRange.Always;
         }
         chartOptions.setVisibleSeriesAsset(series.id, isChecked)  
        }
    });

    sciChartSurface.chartModifiers.add(legend);

    sciChartSurface.chartModifiers.add(
      new ZoomPanModifier({xyDirection: EXyDirection.XDirection}),
      new MouseWheelZoomModifier({xyDirection:EXyDirection.XDirection}),
      new ZoomExtentsModifier({xyDirection:EXyDirection.XyDirection}),
      new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton }),
              new CursorModifier({
          showTooltip: false, 
          hitTestRadius: 10, 
          tooltipContainerBackground: "#424242", 
          axisLabelFill: "#424242", 
          id: "AssetModifierGroup",
          showYLine: true,
          showAxisLabels: true
        }),
              new RolloverModifier({
          hitTestRadius: 10, 
          showTooltip: true, 
          snapToDataPoint: true, 
          showRolloverLine: false, 
          id: "AssetRollOver",
          tooltipDataTemplate: (seriesInfo: any) => {
            const lines: string[] = [];
            lines.push(seriesInfo.seriesName);
            lines.push(`X: ${seriesInfo.formattedXValue}`);
            
            // Only show the Y-axis value that corresponds to this series
            if (seriesInfo.series && seriesInfo.series.yAxisId) {
              const yValue = seriesInfo.formattedYValue;
              // Determine the appropriate label based on the axis ID
              let axisLabel = '';
              if (seriesInfo.series.yAxisId === 'flowBFAxis') {
                axisLabel = ' cc';
              } else if (seriesInfo.series.yAxisId === 'flowAxis') {
                axisLabel = ' cc';
              } else if (seriesInfo.series.yAxisId === 'DefaultAxisId') {
                axisLabel = ' psi';
              } else if (seriesInfo.series.yAxisId === 'Temp') {
                axisLabel = ' °C';
              }
              lines.push(`Y: ${yValue}${axisLabel}`);
            } else {
              lines.push(`Y: ${seriesInfo.formattedYValue}`);
            }
            
            return lines;
          }
        }),
    );
    
    // #endregion
    
    // #region Create Data Series and Setup RenderableSeries

    // Create a series that represents the entire filter period so the chart displays the entire range. 
    const filterPeriodSeries = new FastLineRenderableSeries(wasmContext, {
      id: "FilterPeriodSeriesId", 
      isVisible: false,
      dataSeries: new XyDataSeries(wasmContext, {xValues: [startFilterDateUnix, endFilterDateUnix],yValues: [0,0], dataSeriesName: "filterPeriodSeries"})
    });  

    sciChartSurface.renderableSeries.add(filterPeriodSeries);
    legend.includeSeries(filterPeriodSeries, false);

    const createVolumeDispensedSeries = ((id: string, viewType: string, isVisible: boolean, dataSeries: IDataSeries) => {
      let volumeDispensed;
      
      switch(viewType) {
        case 'bar':
        case 'userBar':
          volumeDispensed = new FastImpulseRenderableSeries(wasmContext, {
            stroke: AUTO_COLOR, fill: AUTO_COLOR,
            pointMarker: new TrianglePointMarker(wasmContext, {width: 12, height: 12, stroke: "rgba(50, 53, 67,1)", fill: "rgba(50, 53, 67,1)"}),
            paletteProvider: new ColumnPaletteProviderActivityChartAsset(),
            id: id,
            dataSeries: dataSeries,
            isVisible: isVisible,
            yAxisId: "flowAxis"
          });
          break;
        case 'line':
        case 'userLine':
        default:
          volumeDispensed = new FastLineRenderableSeries(wasmContext, {
            stroke: AUTO_COLOR,
            pointMarker: new TrianglePointMarker(wasmContext, {width: 12, height: 12, stroke: "rgba(50, 53, 67,1)", fill: "rgba(50, 53, 67,1)"}),
            paletteProvider: new ColumnPaletteProviderActivityChartAsset(),
            id: id,
            dataSeries: dataSeries,
            isVisible: isVisible,
            yAxisId: "flowAxis"
          });
          break;
      }

      volumeDispensed.rolloverModifierProps.tooltipTextColor = 'White'
      volumeDispensed.rolloverModifierProps.showRollover = true
      volumeDispensed.rolloverModifierProps.markerColor = 'red'

      return volumeDispensed;
    });

    for(let i = 0; i < props.chartData.dataChart.length ; i++){
        const endpoint = props.chartData.dataChart[i];
        const isEndpointBF = endpoint.endpointType === 'EndpointBF';
        
        const volumeDispensedDataSeries = new XyDataSeries(wasmContext, {
            xValues: endpoint.timestamps, 
            yValues: endpoint.volumeDispensedData,
            dataSeriesName: props.chartData.dataChart.length > 1 ? endpoint.endpointName  + ` [Volume] (cc)` : `Volume (cc)`
          });
        
        const volumeDispensed = createVolumeDispensedSeries(
          endpoint.endpointId, 
          chartOptions.viewType, 
          chartOptions.visibleSeriesAsset[endpoint.endpointId], 
          volumeDispensedDataSeries
        );

        // Assign the appropriate y-axis based on endpoint type
        if (isEndpointBF && yAxisFlowBF && !allAreEndpointBF) {
          volumeDispensed.yAxisId = "flowBFAxis";
        } else {
          volumeDispensed.yAxisId = "flowAxis";
        }

        sciChartSurface.renderableSeries.add(volumeDispensed);
        legend.includeSeries(volumeDispensed, chartOptions.visibleDataSeries.volume);
    }

    // After the volume dispensed series loop in initChart function
    // Add pressure series for each endpoint
    for(let i = 0; i < props.chartData.dataChart.length; i++) {
      const minMaxColor = theme.strokePalette[i % theme.strokePalette.length];

      const endpoint = props.chartData.dataChart[i];
      const pressureData = endpoint.pressureData;
      if (
        pressureData &&
        Array.isArray(pressureData.timestamps) &&
        pressureData.timestamps.length > 0
      ) {
        // Create pressure min/max band series
        const pressureMinMaxSeries = new FastBandRenderableSeries(wasmContext, {
          dataSeries: new XyyDataSeries(wasmContext, {
            xValues: pressureData.timestamps,
            yValues: pressureData.pressureDataMax,
            y1Values: pressureData.pressureDataMin,
            dataSeriesName: props.chartData.dataChart.length > 1 ? `${endpoint.endpointName} [Pressure Min/Max] (psi)` : `Pressure Min/Max (psi)`
          }),
          stroke: minMaxColor, // This will use the theme's color palette
          strokeY1: minMaxColor,
          fill: minMaxColor + "70",
          fillY1: minMaxColor + "70",
          strokeThickness: 2,
          yAxisId: "DefaultAxisId",
          id: `${endpoint.endpointId}_PressureMinMax`
        });

        // Create pressure mean line series
        const pressureMeanSeries = new FastLineRenderableSeries(wasmContext, {
          stroke: AUTO_COLOR,
          strokeThickness: 2,
          yAxisId: "DefaultAxisId",
          id: `${endpoint.endpointId}_PressureMean`,
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: pressureData.timestamps,
            yValues: pressureData.pressureDataMean,
            dataSeriesName: props.chartData.dataChart.length > 1 ? `${endpoint.endpointName} [Pressure Mean] (psi)` : `Pressure Mean (psi)`
          })
        });

        // Create temperature series
        const pressureTempSeries = new FastLineRenderableSeries(wasmContext, {
          stroke: AUTO_COLOR,
          strokeThickness: 2,
          yAxisId: "Temp",
          id: `${endpoint.endpointId}_PressureTemp`,
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: pressureData.timestamps,
            yValues: pressureData.temperatureData,
            dataSeriesName: props.chartData.dataChart.length > 1 ? `${endpoint.endpointName} [Temperature] (°C)` : `Temperature (°C)`
          })
        });

        // Add all pressure series to the chart
        sciChartSurface.renderableSeries.add(pressureMinMaxSeries);
        sciChartSurface.renderableSeries.add(pressureMeanSeries);
        sciChartSurface.renderableSeries.add(pressureTempSeries);
        

        
        // Use legendConfig to control series visibility in legend
        // legend.includeSeries(pressureMinMaxSeries, chartOptions.legendConfig.pressureMinMax);
        // legend.includeSeries(pressureMeanSeries, chartOptions.legendConfig.pressureMean);
        // legend.includeSeries(pressureTempSeries, chartOptions.legendConfig.temperature);
      }
    }

    // #endregion

    // #region Setup Annotation
    // Add 0 line annotation
    const horizontalLine = new HorizontalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      stroke: 'grey',
      strokeThickness: 3,
      y1: 0,
      yAxisId: "flowAxis",
      annotationLayer: EAnnotationLayer.BelowChart
    });
    sciChartSurface.annotations.add(horizontalLine);
    // #endregion
    
    // #region Setup the API's 
    const setAutoRange = (value: boolean) => {
      UpdateSuspender.using(sciChartSurface, () => {
        // Helper function to get visible series for a specific axis
        const getVisibleSeriesForAxis = (axisId: string) => {
          return sciChartSurface.renderableSeries.asArray().filter(series => 
            series.isVisible && series.yAxisId === axisId
          );
        };

        // Helper function to calculate range from data series
        const calculateRangeFromSeries = (series: IRenderableSeries) => {
          const dataSeries = series.dataSeries as XyDataSeries;
          if (!dataSeries) return null;
          
          const yRange = dataSeries.getWindowedYRange(dataSeries.xRange, true, false);
          return yRange;
        };

        // Helper function to handle axis auto-range
        const handleAxisAutoRange = (axis: AxisBase2D, axisId: string, defaultRange: NumberRange) => {
          const visibleSeries = getVisibleSeriesForAxis(axisId);
          const hasVisibleSeries = visibleSeries.length > 0;

          // Only apply auto-range if there are visible series
          if (hasVisibleSeries) {
            axis.autoRange = value ? EAutoRange.Always : EAutoRange.Never;
            
            if (!value) {
              let maxValue = 0;
              let minValue = 0;
              let isFirst = true;

              visibleSeries.forEach(series => {
                const range = calculateRangeFromSeries(series);
                if (range) {
                  if (isFirst) {
                    minValue = range.min;
                    maxValue = range.max;
                    isFirst = false;
                  } else {
                    minValue = Math.min(minValue, range.min);
                    maxValue = Math.max(maxValue, range.max);
                  }
                }
              });

              // Add padding based on axis type
              if (axisId === "flowAxis") {
                // For flow, always start at 0
                axis.visibleRange = new NumberRange(0, maxValue * 1.2);
              } else if (axisId === "Temp") {
                // For temperature, always start at 0 and add padding to max
                axis.visibleRange = new NumberRange(0, Math.max(maxValue * 1.2, 1));  // Ensure at least 1 degree range
              } else if (axisId === "DefaultAxisId") {
                // For pressure, calculate range based on actual data
                const hasNegativeValues = props.chartData.dataChart.some(endpoint => 
                  endpoint.pressureData?.pressureDataMin?.some(val => val < 0)
                );
                
                if (hasNegativeValues) {
                  // If we have negative values, add padding both ways
                  const padding = (maxValue - minValue) * 0.2;
                  axis.visibleRange = new NumberRange(
                    minValue - padding,
                    maxValue + padding
                  );
                } else {
                  // If no negative values, start at 0
                  axis.visibleRange = new NumberRange(0, maxValue * 1.2);
                }
              }
            }
          } else {
            // If no visible series, enable auto-range but don't set a default range
            axis.autoRange = EAutoRange.Always;
          }
        };

        // Handle each axis
        const flowAxis = sciChartSurface.yAxes.getById("flowAxis");
        if (flowAxis) {
          handleAxisAutoRange(flowAxis, "flowAxis", new NumberRange(0, 10));
        }

        const tempAxis = sciChartSurface.yAxes.getById("Temp");
        if (tempAxis) {
          handleAxisAutoRange(tempAxis, "Temp", new NumberRange(0, 10));
        }

        const pressureAxis = sciChartSurface.yAxes.getById("DefaultAxisId");
        if (pressureAxis) {
          handleAxisAutoRange(pressureAxis, "DefaultAxisId", new NumberRange(0, 100));
        }

        // Handle BF flow axis if it exists
        const flowBFAxis = sciChartSurface.yAxes.getById("flowBFAxis");
        if (flowBFAxis) {
          handleAxisAutoRange(flowBFAxis, "flowBFAxis", new NumberRange(0, 10));
        }

        // Force a redraw to ensure all changes are applied
        sciChartSurface.invalidateElement();
      });
    };

    setAutoRange(chartOptions.isAutoRange);
  

     const changeChartType = (viewType: string) => {
          UpdateSuspender.using(sciChartSurface, () => {
              const renderableSeriesLength = sciChartSurface.renderableSeries.asArray().length;

              for(let i = 0; i < renderableSeriesLength ; i++){
                  const series = sciChartSurface.renderableSeries.get(i);

                  // Only change chart type for volume dispensed series (those without _Pressure or _Temp in their ID)
                  if (series.id !== "FilterPeriodSeriesId" && !series.id.includes('_Pressure') && !series.id.includes('_Temp')) {
                    const newVolumeDispensedSeries = createVolumeDispensedSeries(series.id, viewType, series.isVisible, series.dataSeries);
                    
                    // Preserve the y-axis assignment, defaulting to flowAxis if not set
                    newVolumeDispensedSeries.yAxisId = series.yAxisId || "flowAxis";
                    
                    sciChartSurface.renderableSeries.set(i, newVolumeDispensedSeries);
                  }
              }
          });
          

      }

      const changeDataSeries = (legendConfig: any) => {
        UpdateSuspender.using(sciChartSurface, () => {
          const renderableSeriesLength = sciChartSurface.renderableSeries.asArray().length;
          
          // Track which axes have visible series
          let hasVisibleTempSeries = false;
          let hasVisiblePressureSeries = false;
          let hasVisibleFlowSeries = false;
          let hasVisibleFlowBFSeries = false;

          for (let i = 0; i < renderableSeriesLength; i++) {
            const series = sciChartSurface.renderableSeries.get(i);

            let isVisible = false;
            if (series.id.includes('PressureMinMax')) {
              isVisible = !!legendConfig.pressureMinMax;
              if (isVisible) hasVisiblePressureSeries = true;
            } else if (series.id.includes('PressureMean')) {
              isVisible = !!legendConfig.pressureMean;
              if (isVisible) hasVisiblePressureSeries = true;
            } else if (series.id.includes('PressureTemp')) {
              isVisible = !!legendConfig.temperature;
              if (isVisible) hasVisibleTempSeries = true;
            } else if (
              !series.id.includes('_Pressure') && !series.id.includes('_Temp') && series.id !== 'FilterPeriodSeriesId'
            ) {
              isVisible = !!legendConfig.volume;
              if (isVisible) {
                // Check if this is a BF flow series
                if (series.yAxisId === "flowBFAxis") {
                  hasVisibleFlowBFSeries = true;
                } else {
                  hasVisibleFlowSeries = true;
                }
              }
            }

            series.isVisible = isVisible;

            if (legend) {
              legend.includeSeries(series, isVisible);
            }
          }

          // Update axis visibility based on series visibility
          const tempAxis = sciChartSurface.yAxes.getById("Temp");
          const pressureAxis = sciChartSurface.yAxes.getById("DefaultAxisId");
          const flowAxis = sciChartSurface.yAxes.getById("flowAxis");
          const flowBFAxis = sciChartSurface.yAxes.getById("flowBFAxis");

          if (tempAxis) {
            tempAxis.isVisible = hasVisibleTempSeries;
          }
          if (pressureAxis) {
            pressureAxis.isVisible = hasVisiblePressureSeries;
          }
          if (flowAxis) {
            flowAxis.isVisible = hasVisibleFlowSeries;
          }
          if (flowBFAxis) {
            flowBFAxis.isVisible = hasVisibleFlowBFSeries;
          }

          // After updating visibility, recalculate ranges
          setAutoRange(chartOptions.isAutoRange);
          
          // Force a redraw to ensure all changes are applied
          sciChartSurface.invalidateElement();
        });
      }
      
      // #endregion
   
      sciChartSurface.zoomExtentsY(); // This will adjust the view to show all visible series

      return { sciChartSurface, changeChartType, setAutoRange, changeDataSeries };
  };

  return {initChart, configAfterInit, setAutoRange, changeChartType, changeDataSeries};
}
